<template>
  <div :class="{ 'play-audio-btn': !buttonStyled }">
    <v-btn
      v-if="buttonStyled"
      variant="outlined"
      color="secondary"
      class="mr-4"
      @click.stop="playAudio"
      :aria-label="`${buttonText} ${t(l.button_human_narration)}`"
    >
      <span v-if="buttonText" class="text-none mr-2">{{ buttonText }}</span>
      <FontAwesomeIcon
        v-if="!pausing || (pausing && !isNarrationAudioPlaying)"
        icon="fa-solid fa-volume-high"
      ></FontAwesomeIcon>
      <FontAwesomeIcon v-else icon="fa-solid fa-pause"></FontAwesomeIcon>
    </v-btn>
    <button
      v-else
      :aria-label="t(l.button_human_narration)"
      @click.stop="playAudio"
      @keydown.enter.prevent.stop="playAudio"
      @keydown.space.prevent.stop="playAudio"
    >
      <FontAwesomeIcon icon="fa-solid fa-volume-high"></FontAwesomeIcon>
    </button>
  </div>
</template>

<script setup lang="ts">
import { playAllSound, pauseSound, resumeSound } from '@ilteducation/audio-resources';
import { ref } from 'vue';
import axios from 'axios';
import config from '@/config/ConfigProvider';
import useAudioStore from '@/store/audio';
import { storeToRefs } from 'pinia';
import { AudioResource } from '@ilteducation/videos';
import { useLocale } from '@/plugins/translation';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = withDefaults(
  defineProps<{
    value: AudioResource[] | null;
    buttonStyled?: boolean;
    buttonText?: string;
    pausing?: boolean;
  }>(),
  {
    buttonStyled: false,
    buttonText: '',
    pausing: false,
  },
);

const audioStore = useAudioStore();
const { setNarrationPlaying, updateAudioResourceUrl } = audioStore;
const { isNarrationAudioPlaying } = storeToRefs(audioStore);
const { t, l } = useLocale();

const isPaused = ref(false);

const getUrlForAudio = (id: string, key: string, language: string) =>
  axios.get(`${config.value('contentRegistryUrl')}${id}/contentUrls`).then((res) => {
    if (res.data && res.data.url) {
      updateAudioResourceUrl({ id, url: res.data.url, key, lang: language });
      return res.data.url;
    }
    return '';
  });

const stopNarrationAudioPlaying = () => {
  setNarrationPlaying(false);
};

const playAudio = async () => {
  if (props.pausing && isNarrationAudioPlaying.value) {
    isPaused.value = true;
    setNarrationPlaying(false);
    pauseSound();
  } else if (isPaused.value) {
    isPaused.value = false;
    setNarrationPlaying(true);
    resumeSound();
  } else {
    setNarrationPlaying(true);
    if (props.value?.filter((el: AudioResource) => !!el.url === false).length) {
      const audios = props.value.map((el: AudioResource) => getUrlForAudio(el.id, el.key, el.lang));
      Promise.all(audios).then((urls: string[]) => {
        playAllSound(0, urls, 1, 0, stopNarrationAudioPlaying);
      });
    } else {
      const audios = props.value ? props.value.map((el: AudioResource) => el.url) : [];
      playAllSound(0, audios, 1, 0, stopNarrationAudioPlaying);
    }
  }
};
</script>

<style scoped lang="scss">
.play-audio-btn {
  padding-right: 0.3em;
  &.text-with-narration {
    position: absolute;
    padding: 0;
    top: 0;
    right: 0;
  }
  .v-icon {
    color: #d6dbde;
    &:hover {
      color: #265eb8;
    }
  }
}
</style>
